import { useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { track } from '@vercel/analytics'
import { LogoIcon } from '@talktoast/shared'

interface LocationState {
  name?: string
  email?: string
  position?: number
}

export default function BetaConfirmationPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state as LocationState) || {}
  const [copied, setCopied] = useState(false)

  // Redirect to signup if no state
  useEffect(() => {
    if (!state.name || !state.email) {
      navigate('/beta-signup')
    }
  }, [state, navigate])

  // Track page view
  useEffect(() => {
    track('beta_confirmation_page_viewed', {
      position: state.position,
    })
  }, [state.position])

  const handleShareTwitter = () => {
    const text = encodeURIComponent(
      "I just joined the TalkToast beta waitlist! 🎉 AI-powered meal planning with smart grocery shopping. Join me!"
    )
    const url = encodeURIComponent('https://talktoast.app')
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      '_blank'
    )

    // Track share
    track('beta_waitlist_shared', {
      platform: 'twitter',
    })
  }

  const handleShareFacebook = () => {
    const url = encodeURIComponent('https://talktoast.app')
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      '_blank'
    )

    // Track share
    track('beta_waitlist_shared', {
      platform: 'facebook',
    })
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText('https://talktoast.app')
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)

      // Track copy
      track('beta_waitlist_link_copied')
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  if (!state.name || !state.email) {
    return null
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <LogoIcon size="sm" alt="TalkToast" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                TalkToast
              </h1>
            </div>
            <a
              href="/"
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Back to Home
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          {/* Success Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mb-6 animate-bounce">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Welcome to the Waitlist, {state.name}! 🎉
            </h2>
            <p className="text-lg text-gray-600">
              We've sent a confirmation email to{' '}
              <strong className="text-gray-900">{state.email}</strong>
            </p>
          </div>

          {/* Position Badge */}
          {state.position && (
            <div className="bg-gradient-to-r from-orange-100 to-pink-100 border border-orange-200 rounded-lg p-6 mb-8">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">Your waitlist position</p>
                <p className="text-5xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                  #{state.position}
                </p>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">What happens next?</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <span className="text-orange-600 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Check your email</h4>
                  <p className="text-gray-600 text-sm">
                    We've sent a confirmation to <strong>{state.email}</strong> with all the details.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center">
                  <span className="text-pink-600 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">We'll keep you updated</h4>
                  <p className="text-gray-600 text-sm">
                    You'll receive updates on our progress and an invitation when beta launches.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Beta launch in June 2026</h4>
                  <p className="text-gray-600 text-sm">
                    You'll be among the first to access TalkToast when we launch!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Social Sharing */}
          <div className="border-t border-gray-200 pt-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">
              Help us grow! Share with friends:
            </h3>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={handleShareTwitter}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
                </svg>
                <span>Share on Twitter</span>
              </button>

              <button
                onClick={handleShareFacebook}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                <span>Share on Facebook</span>
              </button>

              <button
                onClick={handleCopyLink}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
                <span>{copied ? 'Link Copied!' : 'Copy Link'}</span>
              </button>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <a
              href="/"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white font-semibold rounded-lg transition-all transform hover:scale-105"
            >
              Explore TalkToast
              <svg
                className="w-5 h-5 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </a>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            Questions? Email us at{' '}
            <a
              href="mailto:support@talktoast.app"
              className="text-orange-600 hover:text-orange-700 font-medium"
            >
              support@talktoast.app
            </a>
          </p>
        </div>
      </main>
    </div>
  )
}
